import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, TrendingUp, TrendingDown, FileDown, FileSpreadsheet, Image } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { useChartExport } from '../../hooks/chart/useChartExport';
import PropTypes from 'prop-types';

const RightSidebar = ({ isOpen, setIsOpen, graphs, graphElements, graphsData }) => {
  const [viewModes, setViewModes] = useState({});
  const [exporting, setExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const { exportSingleChart, exportChartsAsZip, exportChartDataToExcel, exportAllChartsToExcel } = useChartExport();

  const toggleViewMode = (graphId) => {
    setViewModes((previous) => ({
      ...previous,
      [graphId]: previous[graphId] === 'monthly' ? 'daily' : 'monthly'
    }));
  };
  const getViewMode = (graphId) => viewModes[graphId] || 'monthly';

  const getGraphType = (graph) => {
    const data = graphsData?.[graph.id];
    if (!data) return null;
    const hasProduction = data.production?.length > 0;
    const hasConsumption = data.consumption?.length > 0;
    if (hasProduction && hasConsumption) return 'comparison';
    if (hasProduction) return 'production';
    if (hasConsumption) return 'consumption';
    return null;
  };

  // Keep the daily/monthly aggregation and chart-series behavior from the dev branch.
  const prepareChartData = (graph) => {
    const data = graphsData?.[graph.id];
    if (!data) return { chartData: [], uniqueZones: [] };

    const viewMode = getViewMode(graph.id);
    const graphType = getGraphType(graph);
    const allData = [
      ...(data.production || []).map((item) => ({ ...item, type: 'production' })),
      ...(data.consumption || []).map((item) => ({ ...item, type: 'consumption' }))
    ];
    if (allData.length === 0) return { chartData: [], uniqueZones: [] };

    const zones = new Set();
    allData.forEach((item) => {
      if (item?.zoneId == null) return;
      const zoneKey = graphType === 'comparison' ? `${item.zoneId}_${item.type}` : String(item.zoneId);
      zones.add(zoneKey);
    });

    let chartData = [];
    if (viewMode === 'daily') {
      const dailyData = {};
      allData.forEach((item) => {
        if (!item?.date || item.quantite == null || item.zoneId == null) return;
        const dataKey = graphType === 'comparison' ? `${item.zoneId}_${item.type}` : String(item.zoneId);
        if (!dailyData[item.date]) dailyData[item.date] = { date: item.date };
        dailyData[item.date][dataKey] = (dailyData[item.date][dataKey] || 0) + Number(item.quantite);
      });
      chartData = Object.values(dailyData).sort((a, b) => a.date.localeCompare(b.date));
    } else {
      const monthlyData = {};
      allData.forEach((item) => {
        if (!item?.date || item.quantite == null || item.zoneId == null) return;
        const date = new Date(item.date);
        if (Number.isNaN(date.getTime())) return;
        const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const dataKey = graphType === 'comparison' ? `${item.zoneId}_${item.type}` : String(item.zoneId);

        if (!monthlyData[monthYear]) {
          monthlyData[monthYear] = { month: monthYear, counts: {}, totals: {} };
        }
        if (monthlyData[monthYear].totals[dataKey] == null) {
          monthlyData[monthYear].totals[dataKey] = 0;
          monthlyData[monthYear].counts[dataKey] = 0;
        }
        monthlyData[monthYear].totals[dataKey] += Number(item.quantite);
        monthlyData[monthYear].counts[dataKey] += 1;
      });

      chartData = Object.values(monthlyData).map((month) => {
        const dataPoint = { month: month.month };
        Object.keys(month.totals).forEach((dataKey) => {
          dataPoint[dataKey] = month.counts[dataKey] > 0 ? month.totals[dataKey] : 0;
        });
        return dataPoint;
      }).sort((a, b) => a.month.localeCompare(b.month));
    }

    return { chartData, uniqueZones: Array.from(zones) };
  };

  const getColors = (type) => {
    const typeColors = {
      production: ['#82ca9d', '#8884d8', '#ff7300', '#d62728', '#2ca02c'],
      consumption: ['#ff6b6b', '#4ecdc4', '#45b7d1', '#f9c74f', '#f9844a'],
      comparison: ['#82ca9d', '#ff6b6b', '#8884d8', '#4ecdc4', '#ff7300', '#45b7d1']
    };
    return typeColors[type] || typeColors.consumption;
  };

  const visibleGraphs = graphs.filter((graph) => graph.status === 'visible' || graph.status === 'afficher');
  const graphsWithData = visibleGraphs.filter((graph) => graphsData?.[graph.id]);

  const handleExport = async (format) => {
    setExporting(true);
    setExportSuccess(null);
    setShowExportMenu(false);
    try {
      if (format === 'PNG') {
        if (graphsWithData.length === 1) {
          const graph = graphsWithData[0];
          const fileName = `${graph.name.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}`;
          await exportSingleChart(`chart-${graph.id}`, fileName);
          setExportSuccess(`Chart "${graph.name}" exported as PNG`);
        } else {
          await exportChartsAsZip(graphsWithData);
          setExportSuccess(`${graphsWithData.length} charts exported as PNG (ZIP)`);
        }
      } else {
        const chartData = graphsWithData.map((graph) => prepareChartData(graph));
        if (graphsWithData.length === 1) {
          await exportChartDataToExcel(graphsWithData[0], chartData[0].chartData, getGraphType(graphsWithData[0]));
          setExportSuccess(`Data for "${graphsWithData[0].name}" exported to Excel`);
        } else {
          await exportAllChartsToExcel(graphsWithData, chartData);
          setExportSuccess(`${graphsWithData.length} charts exported to Excel`);
        }
      }
      setTimeout(() => setExportSuccess(null), 3000);
    } catch (error) {
      console.error(`Chart export failed (${format}):`, error);
      window.alert(`Chart export failed (${format}).`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <aside className={`bg-gray-50 border-l border-gray-200 transition-all duration-300 ${isOpen ? 'w-72' : 'w-10'}`}>
      <div className="p-3 h-full overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <button type="button" onClick={() => setIsOpen(!isOpen)} className="p-1 rounded hover:bg-gray-200 transition-colors" aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
            {isOpen ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
          </button>
          {isOpen && <h2 className="text-md font-semibold text-gray-800">Statistics</h2>}
        </div>

        {isOpen && (
          <div className="space-y-3">
            {exportSuccess && <div className="bg-green-50 border border-green-200 text-green-600 px-3 py-2 rounded text-xs" role="status"><p className="font-semibold">{exportSuccess}</p></div>}

            {visibleGraphs.map((graph) => {
              const elementsInGraph = graphElements.filter((element) => graph.elements.includes(element.id));
              const hasData = Boolean(graphsData?.[graph.id]);
              const graphType = getGraphType(graph);
              const { chartData, uniqueZones } = prepareChartData(graph);
              const viewMode = getViewMode(graph.id);
              const colors = getColors(graphType);
              const periodField = viewMode === 'monthly' ? 'month' : 'date';
              // In monthly mode, display at most four labels while retaining all monthly data points.
              const xAxisInterval = viewMode === 'monthly'
                ? Math.max(0, Math.ceil(chartData.length / 4) - 1)
                : 'preserveStartEnd';

              return (
                <div key={graph.id} id={`chart-${graph.id}`} className="bg-white p-2 rounded shadow border border-gray-200 text-xs">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-700 truncate">{graph.name}</h3>
                    {hasData && chartData.length > 0 && (
                      <div className="flex items-center space-x-1" aria-label="Choose daily or monthly display">
                        <span className="text-[9px] text-gray-500">D</span>
                        <label className="relative inline-flex items-center cursor-pointer" title="Switch between daily and monthly display">
                          <input type="checkbox" className="sr-only peer" checked={viewMode === 'monthly'} onChange={() => toggleViewMode(graph.id)} aria-label="Switch between daily and monthly display" />
                          <span className="w-7 h-4 bg-gray-200 rounded-full peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-400 peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:after:translate-x-3" />
                        </label>
                        <span className="text-[9px] text-gray-500">M</span>
                      </div>
                    )}
                  </div>

                  {graph.dateDebut && graph.dateFin && <div className="text-[10px] text-gray-500 mb-2">📅 {graph.dateDebut} → {graph.dateFin}</div>}

                  {hasData && chartData.length > 0 ? (
                    <div className="h-40 mb-2">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis
                            dataKey={periodField}
                            tick={{ fontSize: 9 }}
                            interval={xAxisInterval}
                            tickFormatter={(value) => {
                              if (viewMode === 'monthly') {
                                const [year, month] = value.split('-');
                                return `${month}/${year.slice(2)}`;
                              }
                              const date = new Date(value);
                              return Number.isNaN(date.getTime()) ? value : `${date.getDate()}/${date.getMonth() + 1}`;
                            }}
                          />
                          <YAxis tick={{ fontSize: 9 }} width={35} />
                          <Tooltip
                            contentStyle={{ fontSize: '10px' }}
                            formatter={(value) => `${Number(value).toFixed(2)} m³`}
                            labelFormatter={(value) => {
                              if (viewMode === 'monthly') {
                                const [year, month] = value.split('-');
                                return `${month}/${year}`;
                              }
                              const date = new Date(value);
                              return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
                            }}
                          />
                          <Legend
                            wrapperStyle={{ fontSize: '9px' }}
                            formatter={(value) => {
                              if (graphType === 'comparison') {
                                const separator = value.lastIndexOf('_');
                                const zoneId = value.slice(0, separator);
                                const type = value.slice(separator + 1);
                                return `${zoneId} ${type === 'production' ? '(P)' : '(C)'}`;
                              }
                              return value;
                            }}
                          />
                          {uniqueZones.map((zoneId, index) => {
                            const type = zoneId.endsWith('_production') ? 'production' : 'consumption';
                            const paletteIndex = graphType === 'comparison' ? index * 2 + (type === 'consumption' ? 1 : 0) : index;
                            return <Line key={zoneId} type="monotone" dataKey={zoneId} name={zoneId} stroke={colors[paletteIndex % colors.length]} strokeWidth={1.5} dot={{ r: viewMode === 'daily' ? 0 : 2 }} activeDot={{ r: 3 }} connectNulls />;
                          })}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-20 bg-gradient-to-r from-gray-100 to-gray-200 rounded flex items-center justify-center mb-2">
                      <div className="text-center text-gray-500"><div className="text-xs">{elementsInGraph.length} element(s)</div><div className="text-[10px] mt-0.5">{hasData ? 'No data available' : 'Waiting for data'}</div></div>
                    </div>
                  )}

                  <div className="space-y-1 text-[10px] border-t border-gray-200 pt-2">
                    <div className="flex justify-between items-center"><span className="text-green-600 flex items-center"><TrendingUp className="w-3 h-3 mr-1" />Production</span><span className="font-medium">{elementsInGraph.filter((element) => graph.elementSettings?.[element.id]?.showProduction ?? true).length} zone(s)</span></div>
                    <div className="flex justify-between items-center"><span className="text-red-600 flex items-center"><TrendingDown className="w-3 h-3 mr-1" />Consumption</span><span className="font-medium">{elementsInGraph.filter((element) => graph.elementSettings?.[element.id]?.showConsommation ?? true).length} zone(s)</span></div>
                  </div>
                  {hasData && <div className="mt-2 bg-green-50 border border-green-200 text-green-700 px-2 py-1 rounded text-[10px] text-center">✓ Data loaded ({chartData.length} points)</div>}
                </div>
              );
            })}

            {visibleGraphs.length === 0 && <div className="text-gray-500 text-sm">No charts to display. Activate a chart in the left panel.</div>}

            {graphsWithData.length > 0 && (
              <div className="relative">
                <button type="button" onClick={() => setShowExportMenu(!showExportMenu)} disabled={exporting} className={`w-full p-2 rounded shadow-sm transition-shadow border text-xs ${exporting ? 'bg-gray-400 cursor-not-allowed border-gray-500' : 'bg-green-500 hover:bg-green-600 border-gray-300 cursor-pointer'}`}>
                  <span className="flex items-center justify-center space-x-1 text-white">
                    {exporting ? <><span className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full" /><span className="font-medium">Exporting...</span></> : <><FileDown className="w-3 h-3" /><span className="font-medium">Export {graphsWithData.length > 1 ? `(${graphsWithData.length})` : ''}</span></>}
                  </span>
                </button>
                {showExportMenu && !exporting && (
                  <div className="absolute bottom-full mb-1 left-0 right-0 bg-white border border-gray-200 rounded shadow-lg z-10">
                    <button type="button" onClick={() => handleExport('PNG')} className="w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center space-x-2 text-xs border-b border-gray-100"><Image className="w-4 h-4 text-blue-600" /><span>Export as PNG</span></button>
                    <button type="button" onClick={() => handleExport('Excel')} className="w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center space-x-2 text-xs"><FileSpreadsheet className="w-4 h-4 text-green-600" /><span>Export to Excel</span></button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </aside>
  );
};

RightSidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  graphs: PropTypes.array.isRequired,
  graphElements: PropTypes.array.isRequired,
  graphsData: PropTypes.object
};

export default RightSidebar;
